import entityTypes from '@careerstart/wae-common/schema/models/common/enums/entityTypes';
import { DOCUMENT_APPROVAL_STATUSES } from '@careerstart/wae-common/schema/models/document/enums/documentApprovalStatuses';
import documentStateStatuses from '@careerstart/wae-common/schema/models/document/enums/documentStateStatuses';
import documentTypes from '@careerstart/wae-common/schema/models/document/enums/documentTypes';
import { ALL_DOCUMENT_UPLOAD_INTENTS } from '@careerstart/wae-common/schema/models/document/enums/documentUploadIntents';
import { DOCUMENT_VALIDITY_STATUSES } from '@careerstart/wae-common/schema/models/document/enums/documentValidityStatuses';

import { STATUS_BADGE_VARIANTS } from '../../../../../../components/EmployeeDocStatusBadge';

export {
  DOCUMENT_APPROVAL_STATUSES,
  DOCUMENT_VALIDITY_STATUSES,
  documentStateStatuses,
  documentTypes,
  entityTypes,
};

export const STATUS_UI_MAP = {
  [documentStateStatuses.completed]: {
    statusLabelKey: 'userDetail.employeeDocumentation.status.label.completed',
    statusTextKey: 'userDetail.employeeDocumentation.status.text.completed',
    statusVariant: STATUS_BADGE_VARIANTS.GREEN,
  },
  [documentStateStatuses.pendingDocusignSigning]: {
    statusLabelKey: 'userDetail.employeeDocumentation.status.label.pendingDocusignSigning',
    statusTextKey: 'userDetail.employeeDocumentation.status.text.pendingDocusignSigning',
    statusVariant: STATUS_BADGE_VARIANTS.YELLOW,
  },
  [documentStateStatuses.failedDocusignSigning]: {
    statusLabelKey: 'userDetail.employeeDocumentation.status.label.failedDocusignSigning',
    statusTextKey: 'userDetail.employeeDocumentation.status.text.failedDocusignSigning',
    statusVariant: STATUS_BADGE_VARIANTS.RED,
  },
  [documentStateStatuses.pendingUploadToS3]: {
    statusLabelKey: 'userDetail.employeeDocumentation.status.label.pendingUploadToS3',
    statusTextKey: 'userDetail.employeeDocumentation.status.text.pendingUploadToS3',
    statusVariant: STATUS_BADGE_VARIANTS.YELLOW,
  },
  [documentStateStatuses.failedUploadToS3]: {
    statusLabelKey: 'userDetail.employeeDocumentation.status.label.failedUploadToS3',
    statusTextKey: 'userDetail.employeeDocumentation.status.text.failedUploadToS3',
    statusVariant: STATUS_BADGE_VARIANTS.RED,
  },
  [documentStateStatuses.pendingValidation]: {
    statusLabelKey: 'userDetail.employeeDocumentation.status.label.pendingValidation',
    statusTextKey: 'userDetail.employeeDocumentation.status.text.pendingValidation',
    statusVariant: STATUS_BADGE_VARIANTS.YELLOW,
  },
  [documentStateStatuses.failedValidation]: {
    statusLabelKey: 'userDetail.employeeDocumentation.status.label.failedValidation',
    statusTextKey: 'userDetail.employeeDocumentation.status.text.failedValidation',
    statusVariant: STATUS_BADGE_VARIANTS.RED,
  },
  [documentStateStatuses.pendingApproval]: {
    statusLabelKey: 'userDetail.employeeDocumentation.status.label.pendingApproval',
    statusTextKey: 'userDetail.employeeDocumentation.status.text.pendingApproval',
    statusVariant: STATUS_BADGE_VARIANTS.YELLOW,
  },
  [documentStateStatuses.failedApproval]: {
    statusLabelKey: 'userDetail.employeeDocumentation.status.label.failedApproval',
    statusTextKey: 'userDetail.employeeDocumentation.status.text.failedApproval',
    statusVariant: STATUS_BADGE_VARIANTS.RED,
  },
  [documentStateStatuses.voided]: {
    statusLabelKey: 'userDetail.employeeDocumentation.status.label.voided',
    statusTextKey: 'userDetail.employeeDocumentation.status.text.voided',
    statusVariant: STATUS_BADGE_VARIANTS.RED,
  },
  UNKNOWN: {
    statusLabelKey: 'userDetail.employeeDocumentation.status.label.unknown',
    statusTextKey: 'userDetail.employeeDocumentation.status.text.unknown',
    statusVariant: STATUS_BADGE_VARIANTS.BLACK,
  },
};

// The I-9 identity and work-authorization evidence a worker may submit, including the
// driver's license sides. Listed explicitly, and ordered to match the server's
// I9_IDENTITY_EVIDENCE_FIELD_IDS, so a document type added to wae-common does not join
// this section until someone decides it belongs here.
export const I9_SUPPORT_DOCUMENT_TYPES = [
  documentTypes.US_PASSPORT_ID_PAGE,
  documentTypes.US_PASSPORT_BARCODE_PAGE,
  documentTypes.US_PASSPORT_CARD_FRONT,
  documentTypes.US_PASSPORT_CARD_BACK,
  documentTypes.PERMANENT_RESIDENT_CARD_FRONT,
  documentTypes.PERMANENT_RESIDENT_CARD_BACK,
  documentTypes.FORM_I551_FRONT,
  documentTypes.FORM_I551_BACK,
  documentTypes.FOREIGN_PASSPORT,
  documentTypes.FOREIGN_PASSPORT_VISA_OR_STAMP_PAGE,
  documentTypes.FORM_I94,
  documentTypes.FORM_I94A,
  documentTypes.DRIVERS_LICENSE_FRONT,
  documentTypes.DRIVERS_LICENSE_BACK,
  documentTypes.US_GOVERNMENT_ID_FRONT,
  documentTypes.US_GOVERNMENT_ID_BACK,
  documentTypes.SCHOOL_ID_FRONT,
  documentTypes.SCHOOL_ID_BACK,
  documentTypes.VOTER_REGISTRATION_CARD,
  documentTypes.US_MILITARY_CARD_FRONT,
  documentTypes.US_MILITARY_CARD_BACK,
  documentTypes.MILITARY_DEPENDENT_ID_CARD_FRONT,
  documentTypes.MILITARY_DEPENDENT_ID_CARD_BACK,
  documentTypes.US_COAST_GUARD_MERCHANT_MARINER_CARD_FRONT,
  documentTypes.US_COAST_GUARD_MERCHANT_MARINER_CARD_BACK,
  documentTypes.NATIVE_AMERICAN_TRIBAL_DOCUMENT_FRONT,
  documentTypes.NATIVE_AMERICAN_TRIBAL_DOCUMENT_BACK,
  documentTypes.CANADIAN_DRIVERS_LICENSE_FRONT,
  documentTypes.CANADIAN_DRIVERS_LICENSE_BACK,
  documentTypes.SOCIAL_SECURITY_CARD,
  documentTypes.SOCIAL_SECURITY_CARD_BACK,
  documentTypes.DS_CERTIFICATION_OF_REPORT_OF_BIRTH,
  documentTypes.DS_CERTIFICATION_OF_REPORT_OF_BIRTH_BACK,
  documentTypes.BIRTH_CERTIFICATE,
  documentTypes.US_CITIZEN_ID_CARD_FRONT,
  documentTypes.US_CITIZEN_ID_CARD_BACK,
  documentTypes.RESIDENT_CITIZEN_ID_CARD_FRONT,
  documentTypes.RESIDENT_CITIZEN_ID_CARD_BACK,
  documentTypes.EMPLOYMENT_AUTHORIZATION_DOCUMENT_FRONT,
  documentTypes.EMPLOYMENT_AUTHORIZATION_DOCUMENT_BACK,
];

// NY IT-2104 and IT-2104-E. Listed explicitly, matching the server's
// onboardingDocumentTypeStrategy entries, so a document type added to wae-common
// does not join this section until someone decides it belongs here. The pay
// acknowledgement is a wage notice, not a tax form, and stays out of this list.
export const STATE_TAX_DOCUMENT_TYPES = [
  documentTypes.NYS_WITHHOLDING_CERTIFICATE,
  documentTypes.NYS_WITHHOLDING_EXEMPTION_CERTIFICATE,
];

// Onboarding document sections rendered on the initial view, sourced from
// documents/read-onboarding-docs. "other" uses `types: null` because it is
// special-cased to read the response's `uncategorized` bucket directly.
export const DOCUMENT_SECTIONS = [
  {
    key: 'profilePicture',
    titleKey: 'userDetail.employeeDocumentation.section.profilePicture',
    types: [documentTypes.PROFILE_PICTURE],
  },
  {
    key: 'w4Form',
    titleKey: 'userDetail.employeeDocumentation.section.w4Form',
    types: [documentTypes.W4_FORM],
  },
  {
    key: 'i9Form',
    titleKey: 'userDetail.employeeDocumentation.section.i9Form',
    types: [documentTypes.I9_FORM],
  },
  {
    key: 'i9SupportDocuments',
    titleKey: 'userDetail.employeeDocumentation.section.i9SupportDocuments',
    types: I9_SUPPORT_DOCUMENT_TYPES,
  },
  {
    key: 'stateTaxDocuments',
    titleKey: 'userDetail.employeeDocumentation.section.stateTaxDocuments',
    types: STATE_TAX_DOCUMENT_TYPES,
  },
  {
    key: 'aboldcoDocuments',
    titleKey: 'userDetail.employeeDocumentation.section.aboldcoDocuments',
    types: [documentTypes.W2_ELECTRONIC_CONSENT],
  },
  {
    key: 'other',
    titleKey: 'userDetail.employeeDocumentation.section.other',
    types: null,
  },
];

// Friendly label per documentType, used for DocumentCard titles. Falls back to the
// raw documentType string for anything unmapped (e.g. stray uncategorized types).
export const DOCUMENT_TYPE_LABEL_KEYS = {
  [documentTypes.PROFILE_PICTURE]: 'userDetail.employeeDocumentation.docType.profilePicture',
  [documentTypes.DRIVERS_LICENSE_FRONT]:
    'userDetail.employeeDocumentation.docType.driversLicenseFront',
  [documentTypes.DRIVERS_LICENSE_BACK]:
    'userDetail.employeeDocumentation.docType.driversLicenseBack',
  [documentTypes.W4_FORM]: 'userDetail.employeeDocumentation.docType.w4Form',
  [documentTypes.I9_FORM]: 'userDetail.employeeDocumentation.docType.i9Form',
  [documentTypes.W2_ELECTRONIC_CONSENT]:
    'userDetail.employeeDocumentation.docType.w2ElectronicConsent',
  [documentTypes.NYS_PAY_ACKNOWLEDGEMENT]:
    'userDetail.employeeDocumentation.docType.nysPayAcknowledgement',
  [documentTypes.NYS_WITHHOLDING_CERTIFICATE]:
    'userDetail.employeeDocumentation.docType.nysWithholdingCertificate',
  [documentTypes.NYS_WITHHOLDING_EXEMPTION_CERTIFICATE]:
    'userDetail.employeeDocumentation.docType.nysWithholdingExemptionCertificate',
  [documentTypes.SEXUAL_HARASSMENT_SURVEY]:
    'userDetail.employeeDocumentation.docType.sexualHarassmentSurvey',
  [documentTypes.EMPLOYEE_HANDBOOK]: 'userDetail.employeeDocumentation.docType.employeeHandbook',
  [documentTypes.GROUP_HEALTH_INSURANCE_WAIVER]:
    'userDetail.employeeDocumentation.docType.groupHealthInsuranceWaiver',
  [documentTypes.WOTC_SCREENING]: 'userDetail.employeeDocumentation.docType.wotcScreening',
  [documentTypes.ATTENDANCE_POLICY]: 'userDetail.employeeDocumentation.docType.attendancePolicy',
  // I-9 identity and work-authorization evidence. Two-sided documents carry a row per
  // side because signing requirements presign each side as its own upload.
  [documentTypes.US_PASSPORT_ID_PAGE]: 'userDetail.employeeDocumentation.docType.usPassportIdPage',
  [documentTypes.US_PASSPORT_BARCODE_PAGE]:
    'userDetail.employeeDocumentation.docType.usPassportBarcodePage',
  [documentTypes.US_PASSPORT_CARD_FRONT]:
    'userDetail.employeeDocumentation.docType.usPassportCardFront',
  [documentTypes.US_PASSPORT_CARD_BACK]:
    'userDetail.employeeDocumentation.docType.usPassportCardBack',
  [documentTypes.PERMANENT_RESIDENT_CARD_FRONT]:
    'userDetail.employeeDocumentation.docType.permanentResidentCardFront',
  [documentTypes.PERMANENT_RESIDENT_CARD_BACK]:
    'userDetail.employeeDocumentation.docType.permanentResidentCardBack',
  [documentTypes.FORM_I551_FRONT]: 'userDetail.employeeDocumentation.docType.formI551Front',
  [documentTypes.FORM_I551_BACK]: 'userDetail.employeeDocumentation.docType.formI551Back',
  [documentTypes.FOREIGN_PASSPORT]: 'userDetail.employeeDocumentation.docType.foreignPassport',
  [documentTypes.FOREIGN_PASSPORT_VISA_OR_STAMP_PAGE]:
    'userDetail.employeeDocumentation.docType.foreignPassportVisaOrStampPage',
  [documentTypes.FORM_I94]: 'userDetail.employeeDocumentation.docType.formI94',
  [documentTypes.FORM_I94A]: 'userDetail.employeeDocumentation.docType.formI94A',
  [documentTypes.US_GOVERNMENT_ID_FRONT]:
    'userDetail.employeeDocumentation.docType.usGovernmentIdFront',
  [documentTypes.US_GOVERNMENT_ID_BACK]:
    'userDetail.employeeDocumentation.docType.usGovernmentIdBack',
  [documentTypes.SCHOOL_ID_FRONT]: 'userDetail.employeeDocumentation.docType.schoolIdFront',
  [documentTypes.SCHOOL_ID_BACK]: 'userDetail.employeeDocumentation.docType.schoolIdBack',
  [documentTypes.VOTER_REGISTRATION_CARD]:
    'userDetail.employeeDocumentation.docType.voterRegistrationCard',
  [documentTypes.US_MILITARY_CARD_FRONT]:
    'userDetail.employeeDocumentation.docType.usMilitaryCardFront',
  [documentTypes.US_MILITARY_CARD_BACK]:
    'userDetail.employeeDocumentation.docType.usMilitaryCardBack',
  [documentTypes.MILITARY_DEPENDENT_ID_CARD_FRONT]:
    'userDetail.employeeDocumentation.docType.militaryDependentIdCardFront',
  [documentTypes.MILITARY_DEPENDENT_ID_CARD_BACK]:
    'userDetail.employeeDocumentation.docType.militaryDependentIdCardBack',
  [documentTypes.US_COAST_GUARD_MERCHANT_MARINER_CARD_FRONT]:
    'userDetail.employeeDocumentation.docType.usCoastGuardMerchantMarinerCardFront',
  [documentTypes.US_COAST_GUARD_MERCHANT_MARINER_CARD_BACK]:
    'userDetail.employeeDocumentation.docType.usCoastGuardMerchantMarinerCardBack',
  [documentTypes.NATIVE_AMERICAN_TRIBAL_DOCUMENT_FRONT]:
    'userDetail.employeeDocumentation.docType.nativeAmericanTribalDocumentFront',
  [documentTypes.NATIVE_AMERICAN_TRIBAL_DOCUMENT_BACK]:
    'userDetail.employeeDocumentation.docType.nativeAmericanTribalDocumentBack',
  [documentTypes.CANADIAN_DRIVERS_LICENSE_FRONT]:
    'userDetail.employeeDocumentation.docType.canadianDriversLicenseFront',
  [documentTypes.CANADIAN_DRIVERS_LICENSE_BACK]:
    'userDetail.employeeDocumentation.docType.canadianDriversLicenseBack',
  [documentTypes.SOCIAL_SECURITY_CARD]:
    'userDetail.employeeDocumentation.docType.socialSecurityCard',
  [documentTypes.SOCIAL_SECURITY_CARD_BACK]:
    'userDetail.employeeDocumentation.docType.socialSecurityCardBack',
  [documentTypes.DS_CERTIFICATION_OF_REPORT_OF_BIRTH]:
    'userDetail.employeeDocumentation.docType.dsCertificationOfReportOfBirth',
  [documentTypes.DS_CERTIFICATION_OF_REPORT_OF_BIRTH_BACK]:
    'userDetail.employeeDocumentation.docType.dsCertificationOfReportOfBirthBack',
  [documentTypes.BIRTH_CERTIFICATE]: 'userDetail.employeeDocumentation.docType.birthCertificate',
  [documentTypes.US_CITIZEN_ID_CARD_FRONT]:
    'userDetail.employeeDocumentation.docType.usCitizenIdCardFront',
  [documentTypes.US_CITIZEN_ID_CARD_BACK]:
    'userDetail.employeeDocumentation.docType.usCitizenIdCardBack',
  [documentTypes.RESIDENT_CITIZEN_ID_CARD_FRONT]:
    'userDetail.employeeDocumentation.docType.residentCitizenIdCardFront',
  [documentTypes.RESIDENT_CITIZEN_ID_CARD_BACK]:
    'userDetail.employeeDocumentation.docType.residentCitizenIdCardBack',
  [documentTypes.EMPLOYMENT_AUTHORIZATION_DOCUMENT_FRONT]:
    'userDetail.employeeDocumentation.docType.employmentAuthorizationDocumentFront',
  [documentTypes.EMPLOYMENT_AUTHORIZATION_DOCUMENT_BACK]:
    'userDetail.employeeDocumentation.docType.employmentAuthorizationDocumentBack',
  [documentTypes.OTHER]: 'userDetail.employeeDocumentation.docType.other',
};

// documentTypes whose onboarding selection strategy is "latestPerYear" (up to 2 results),
// so their card title gets a calendar-year suffix to disambiguate the rows.
export const YEAR_SUFFIXED_DOCUMENT_TYPES = [documentTypes.W4_FORM, documentTypes.I9_FORM];

// Select options for the upload modal's document-type field. Sourced from
// ALL_DOCUMENT_UPLOAD_INTENTS, which matches documentTypes.all. The wae-common enum
// stays the single source of truth for this list, so it updates here automatically
// when it changes.
export const UPLOAD_DOCUMENT_TYPE_OPTIONS = ALL_DOCUMENT_UPLOAD_INTENTS.map((type) => ({
  value: type,
  labelKey: DOCUMENT_TYPE_LABEL_KEYS[type] || type,
}));

export const PRESIGN_UPLOAD_ROUTE = 'documents/s3/presign-upload-url';
export const PRESIGN_DOWNLOAD_ROUTE = 'documents/s3/presign-download-url';
export const SET_APPROVAL_ROUTE = 'documents/set-approval';
export const SET_VALIDITY_ROUTE = 'documents/set-validity';
export const SET_VALIDITY_RANGE_ROUTE = 'documents/set-validity-range';
export const VOID_DOCUMENT_ROUTE = 'documents/void';

export const DOCUMENT_ACTION_SNACKBAR_KEYS = {
  approval: {
    success: 'userDetail.employeeDocumentation.statusChange.success',
    error: 'userDetail.employeeDocumentation.statusChange.error',
  },
  validity: {
    success: 'userDetail.employeeDocumentation.validityChange.success',
    error: 'userDetail.employeeDocumentation.validityChange.error',
  },
  expiration: {
    success: 'userDetail.employeeDocumentation.expiration.success',
    error: 'userDetail.employeeDocumentation.expiration.error',
  },
  delete: {
    success: 'userDetail.employeeDocumentation.delete.success',
    error: 'userDetail.employeeDocumentation.delete.error',
  },
};

export const UPLOAD_ACCEPT = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

export const UPLOAD_MAX_SIZE_MB = 10;

export const UPLOAD_STATUS = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  ERROR: 'error',
};
